import React from "react";
import cn from "clsx";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/redux/store";
import { useIsRTL, useRouteSlug } from "@/hooks";
import useIsSmallScreen from "@/hooks/useIsSmallScreen";
import "./style.scss";

const TOTAL_STEPS = 6;
const RADIUS = 22;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const GRADIENT_ID = "pcb-homepage-grad";

const UserProfileCompletionBlock: React.FC = () => {
  const history = useHistory();
  const routeSlug = useRouteSlug();
  const { t } = useTranslation("common");
  const isRTL = useIsRTL();
  const { isSmallScreen } = useIsSmallScreen();

  const profileCompletionPercentage =
    useAppSelector(state => state?.authReducer?.profileCompletionPercentage) ||
    0;

  const completedSteps = Math.min(
    TOTAL_STEPS,
    Math.round((profileCompletionPercentage / 100) * TOTAL_STEPS)
  );
  const stepsLeft = TOTAL_STEPS - completedSteps;
  const strokeOffset =
    CIRCUMFERENCE - (profileCompletionPercentage / 100) * CIRCUMFERENCE;

  const handleContinue = () => {
    history.push(`/${routeSlug}/my-account/my-details/`);
  };

  if (isSmallScreen) {
    return (
      <div className={cn("pcb-mob-wrapper", { arabic: isRTL })}>
        <div className="pcb-mob-inner">
          <div className="pcb-mob-circle">
            <svg width="56" height="56" viewBox="0 0 56 56">
              <defs>
                <linearGradient
                  id={GRADIENT_ID}
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#7b5cf5" />
                  <stop offset="40%" stopColor="#d040a0" />
                  <stop offset="70%" stopColor="#f4772a" />
                  <stop offset="100%" stopColor="#f5c018" />
                </linearGradient>
              </defs>
              <circle cx="28" cy="28" r={RADIUS} fill="white" />
              <circle
                cx="28"
                cy="28"
                r={RADIUS}
                fill="none"
                stroke="#e9e9e9"
                strokeWidth="4"
              />
              <circle
                cx="28"
                cy="28"
                r={RADIUS}
                fill="none"
                stroke={`url(#${GRADIENT_ID})`}
                strokeWidth="4"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={strokeOffset}
                strokeLinecap="round"
                transform="rotate(-90 28 28)"
              />
              <text
                x="28"
                y="33"
                textAnchor="middle"
                fontSize="12"
                fontWeight="bold"
                fill="#000"
                fontFamily="inherit"
              >
                {profileCompletionPercentage}%
              </text>
            </svg>
          </div>

          <div className="pcb-mob-content">
            <div className="pcb-mob-header">
              <span className="pcb-mob-title">
                {t("profileCompletion.completeYourProfile")}
              </span>
              {stepsLeft > 0 && (
                <span className="pcb-mob-badge">
                  {t("profileCompletion.stepsLeft", { count: stepsLeft })}
                </span>
              )}
            </div>
            <div className="pcb-mob-subtitle">
              {t("profileCompletion.earnPrefix")}
              <strong>{t("profileCompletion.smilesPoints")}</strong>
            </div>
            <div className="pcb-mob-dashes">
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <span
                  key={i}
                  className={cn("pcb-mob-dash", {
                    "pcb-mob-dash--filled": i < completedSteps
                  })}
                />
              ))}
            </div>
          </div>

          <button
            className="pcb-mob-arrow"
            type="button"
            onClick={handleContinue}
          >
            <svg
              width="7"
              height="11"
              viewBox="0 0 7 11"
              fill="none"
              style={isRTL ? { transform: "rotate(180deg)" } : undefined}
            >
              <path
                d="M1 1L6 5.5L1 10"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("pcb-wrapper", { arabic: isRTL })}>
      <div className="pcb-inner">
        <div className="pcb-left">
          <div className="pcb-title">
            {t("profileCompletion.completeYourProfile")}
          </div>
          <div className="pcb-subtitle">
            {t("profileCompletion.earnPrefix")}
            <strong>{t("profileCompletion.smilesPoints")}</strong>
          </div>
        </div>

        <div className="pcb-progress-block">
          <div className="pcb-steps-text">
            {t("profileCompletion.stepsComplete", {
              completed: completedSteps,
              total: TOTAL_STEPS
            })}
            {" • "}
            <strong>{profileCompletionPercentage}%</strong>
          </div>
          <div className="pcb-dashes">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span
                key={i}
                className={cn("pcb-dash", {
                  "pcb-dash--filled": i < completedSteps
                })}
              />
            ))}
          </div>
        </div>

        <button className="pcb-btn" type="button" onClick={handleContinue}>
          {t("profileCompletion.continueSetup")}
        </button>
      </div>
    </div>
  );
};

export default UserProfileCompletionBlock;
